import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CenterModel } from './center.model';
import { CenterService } from './center.service';
import { AdministrativeCentresModel } from '../contact/administrativeCentres.model';
import { Utils } from '../../../shared/utils';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

export interface CenterFormDialogData {
  uid: string | null;
  contactUid: string;
  showFaceFields: boolean;
}

export type CenterFaceKey = 'oficinaContable' | 'organoGestor' | 'unidadTramitadora';

export interface CenterFaceBlock {
  key: CenterFaceKey;
  label: string;
}

const FACE_ROLES: Record<CenterFaceKey, string> = { oficinaContable: '01', organoGestor: '02', unidadTramitadora: '03' };
const FACE_DEFAULT_NAMES: Record<CenterFaceKey, string> = { oficinaContable: 'Oficina Contable', organoGestor: 'Órgano Gestor', unidadTramitadora: 'Unidad Tramitadora' };
const FACE_CODE_MAX = 10;
const FACE_NAME_MAX = 40;

@Component({
    selector: 'convfact-center-form',
    templateUrl: './center-form.component.html',
    styleUrls: ['./center.component.css'],
    standalone: false
})
export class CenterFormComponent implements OnInit {
  @Input() uid: string | null = null;
  @Input() contactUid: string;
  @Input() showFaceFields = false;
  @Output() saved = new EventEmitter<CenterModel>();
  @Output() cancelled = new EventEmitter<void>();

  readonly faceBlocks: CenterFaceBlock[] = [
    { key: 'oficinaContable', label: 'Oficina contable' },
    { key: 'organoGestor', label: 'Órgano gestor' },
    { key: 'unidadTramitadora', label: 'Unidad tramitadora' },
  ];
  readonly faceCodeMax = FACE_CODE_MAX;
  readonly faceNameMax = FACE_NAME_MAX;

  center: CenterModel = new CenterModel();
  face: Record<CenterFaceKey, AdministrativeCentresModel> = {
    oficinaContable: CenterFormComponent.faceCentre('oficinaContable'),
    organoGestor: CenterFormComponent.faceCentre('organoGestor'),
    unidadTramitadora: CenterFormComponent.faceCentre('unidadTramitadora'),
  };

  ready = false;
  saving = false;
  submitted = false;

  static open(dialog: MatDialog, data: CenterFormDialogData): Observable<boolean> {
    return dialog.open<CenterFormComponent, CenterFormDialogData, boolean>(CenterFormComponent, {
      width: data.showFaceFields ? '680px' : '600px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  private static faceCentre(key: CenterFaceKey): AdministrativeCentresModel {
    const centre = new AdministrativeCentresModel();
    centre.roleTypeCode = FACE_ROLES[key];
    return centre;
  }

  constructor(
    private centerService: CenterService,
    private notify: NotificationService,
    @Optional() public dialogRef: MatDialogRef<CenterFormComponent, boolean>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CenterFormDialogData,
  ) {
    if (data) {
      this.uid = data.uid || null;
      this.contactUid = data.contactUid;
      this.showFaceFields = data.showFaceFields === true;
    }
  }

  get isEdit(): boolean {
    return !!this.uid;
  }

  get missingName(): boolean {
    return this.isBlank(this.center.name);
  }

  get faceUsed(): boolean {
    return this.faceBlocks.some(block => !this.isBlank(this.face[block.key].centreCode));
  }

  get faceIncomplete(): boolean {
    return this.faceBlocks.some(block => this.missingFaceCode(block) || this.missingFaceName(block));
  }

  get faceTooLong(): boolean {
    return this.faceBlocks.some(block => this.tooLongCode(block) || this.tooLongName(block));
  }

  ngOnInit(): void {
    if (!this.isEdit) {
      this.center.contactUid = this.contactUid;
      this.applyDefaultFaceNames();
      this.ready = true;
      return;
    }
    this.centerService.loadData(this.uid)
      .then(center => {
        if (!center) throw new Error();
        this.center = center;
        (center.administrativeCentres || []).forEach(centre => {
          const block = this.faceBlocks.find(b => FACE_ROLES[b.key] === centre.roleTypeCode);
          if (block) this.face[block.key] = centre;
        });
        this.applyDefaultFaceNames();
        this.ready = true;
      })
      .catch(error => {
        this.notify.error(error?.error?.message || 'No se pudo cargar el centro');
        this.cancel();
      });
  }

  missingFaceCode(block: CenterFaceBlock): boolean {
    return this.showFaceFields && this.faceUsed && this.isBlank(this.face[block.key].centreCode);
  }

  missingFaceName(block: CenterFaceBlock): boolean {
    return this.showFaceFields && this.faceUsed && this.isBlank(this.face[block.key].centreName);
  }

  tooLongCode(block: CenterFaceBlock): boolean {
    return this.showFaceFields && (this.face[block.key].centreCode || '').length > FACE_CODE_MAX;
  }

  tooLongName(block: CenterFaceBlock): boolean {
    return this.showFaceFields && (this.face[block.key].centreName || '').length > FACE_NAME_MAX;
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (this.missingName) return;
    if (this.faceIncomplete) {
      this.notify.error('Completa todos los códigos y nombres FACe o deja los códigos en blanco');
      return;
    }
    if (this.faceTooLong) {
      this.notify.error('Revisa los campos FACe marcados en rojo');
      return;
    }

    const payload: CenterModel = { ...this.center, contactUid: this.center.contactUid || this.contactUid };
    payload.administrativeCentres = this.showFaceFields
      ? (this.faceUsed ? [this.face.organoGestor, this.face.unidadTramitadora, this.face.oficinaContable] : [])
      : (this.center.administrativeCentres || []);

    this.saving = true;
    if (this.dialogRef) this.dialogRef.disableClose = true;

    const request = this.isEdit
      ? this.centerService.editData(this.uid, payload)
      : this.centerService.addData(payload);

    request
      .then(result => {
        this.notify.ok(this.isEdit ? 'Centro actualizado' : 'Centro creado');
        if (this.dialogRef) {
          this.dialogRef.disableClose = false;
          this.dialogRef.close(true);
          return;
        }
        this.saving = false;
        this.saved.emit(result);
      })
      .catch(error => {
        this.saving = false;
        if (this.dialogRef) this.dialogRef.disableClose = false;
        this.notify.error(error?.error?.message || 'No se pudo guardar el centro');
      });
  }

  cancel(): void {
    if (this.saving) return;
    if (this.dialogRef) {
      this.dialogRef.close(false);
      return;
    }
    this.cancelled.emit();
  }

  private applyDefaultFaceNames(): void {
    if (!this.showFaceFields) return;
    this.faceBlocks.forEach(block => {
      if (this.isBlank(this.face[block.key].centreName)) this.face[block.key].centreName = FACE_DEFAULT_NAMES[block.key];
    });
  }

  private isBlank(value: string): boolean {
    return Utils.IsNullOrEmpty((value || '').trim());
  }
}
